import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../hooks';
import styles from './Admin.module.css';

export const Route = createFileRoute('/admin/tools')({
  component: AdminToolsRoute,
});

function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

const HEX64 = /^[0-9a-f]{64}$/i;

// Orphan PoolDOs surfaced from wrangler tail on dev, 2026-04-14.
// See workers/gateway/scripts/orphan-pool-dos-2026-04-14.json.
const SEED_ORPHANS = [
  '296f2963c44f3e3889da53f39c8d933b6bf89d0a97f5edc709dbc9f336fb445c',
  '9e621773efb734058336a91d0eae5d99f1356712a1554640101cef52c0b96ffb',
  '9b32b5ce567924c203c67671d11da14744657d71433c842ea3c564483be6f0e1',
  '7fd7623b23431b0c80d2b062653556cf9cd545a8130944e9f00e5d1e5a1f4e87',
  '54716a0d6a83b26db0abb7ddf0b17301c5454586d4c26dbf09452bdd2ec900ff',
  '17edd11c6381f30d69974d975a1c20f6e0bec844c139d051ff8d97c7f085ed35',
  '1cfb11cc9c693828d16ef2510bf13d164d851a4bbc76809b20aa41d3dcf85af1',
  'efb962f1e2ee04f39fa8f1996989207cffb3485de674f014f93b07f9eed259b8',
  '8ac4e2fc398ea11ffe38531370106483510f96102279539c1ecd32e28ff1d4f5',
  '7a357d98b3b15e8043d091c518de7c43e111f3fb296f87701fa1bbf55f92ef25',
  '413628a571269dbfe65fc871a31dd269753e664d71d731a52ea2f6ac721f311d',
  '13b277d8586975b782501548be3555618f515b2568a8b4e788b4e58f6771d8b9',
  '03b42fcce7aa71b90c57a8883c295bc88283ba14dc94b8f5a2e434e8b6700e14',
  'e110338908c558352ab3928174a0e5aca95989a5813806cf862ae90f98fdf383',
  '7892e157208a49ca29e2e601341efe5a51e395155de6f68ae3a57d640a8a04fb',
  'ac69ac1317fa732c527f7e38c053e650bc1b4cdf70c65fffbae9fd4137b8582d',
  '75b89b3feb601bf126fab9c33cc12c3278a1c34f91eb3fa10912d3e3adf7f1de',
  '694e51232c88c928e767cb37f03d21f28a4dc8a749fc8d4f032e76cf4c7d04b2',
  '5e4803c40e66d41a3b2aacf26df08494808a345c714e807641ffb610f7a5e3a0',
  '595e7e7477a657067e27aa5270031d2d824fdd322a391efaf5257900e3f5c403',
  '3bb6dc46a05078297932b82d732b3be1888683607a886b0f3074305d18f38fc2',
  '22b1c8b25ef50fc7adab8e2d8d2d269c1bac7fe46cae74f9baab1b71afbc86eb',
];

interface PruneResult {
  doId: string;
  ok: boolean;
  status?: number;
  dryRun?: boolean;
  error?: string;
  hadAlarm?: boolean;
  nameIdentity?: { orgId: string; poolId: string } | null;
  storedIdentity?: { orgId: string; poolId: string } | null;
}

interface PruneSummary {
  requested: number;
  wiped: number;
  refused: number;
  errors: number;
  dryRun: boolean;
}

function AdminToolsRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [input, setInput] = useState(SEED_ORPHANS.join('\n'));
  const [dryRun, setDryRun] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<PruneSummary | null>(null);
  const [results, setResults] = useState<PruneResult[]>([]);

  useEffect(() => {
    if (isPlatformAdmin === false) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  const { doIds, invalidLines } = useMemo(() => {
    const lines = input
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const l of lines) {
      if (HEX64.test(l)) valid.push(l.toLowerCase());
      else invalid.push(l);
    }
    return { doIds: valid, invalidLines: invalid };
  }, [input]);

  const run = useCallback(async () => {
    if (doIds.length === 0) {
      setError('Provide at least one valid 64-char hex DO id');
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        `Wipe ${doIds.length} PoolDO(s)? This cannot be undone. The DO will refuse if it has a resolvable identity.`,
      );
      if (!ok) return;
    }

    try {
      setRunning(true);
      setError(null);
      setSummary(null);
      setResults([]);

      const token = await getToken({ template: 'default' });
      if (!token) throw new Error('Authentication required');

      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/prune-orphans`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doIds, dryRun }),
      });
      const text = await res.text();
      let json: any = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        // fall through with raw text
      }
      if (!res.ok) {
        throw new Error(json?.error || `HTTP ${res.status}: ${text.slice(0, 200)}`);
      }
      setSummary(json.summary);
      setResults(json.results ?? []);
    } catch (err: any) {
      setError(err.message || 'Request failed');
    } finally {
      setRunning(false);
    }
  }, [doIds, dryRun, getToken]);

  if (isPlatformAdmin === undefined) return null;
  if (!isPlatformAdmin) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Tools</h1>
      </div>

      <section style={{ marginBottom: 'var(--space-6)' }}>
        <h2
          className={styles.title}
          style={{ fontSize: 'var(--text-md)', marginBottom: 'var(--space-2)' }}
        >
          Prune Orphan PoolDOs
        </h2>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            marginBottom: 'var(--space-4)',
            maxWidth: 720,
          }}
        >
          Wipes Durable Object storage for PoolDOs that have no resolvable
          identity (no DO name, no stored identity row). The DO refuses
          (HTTP 409) if it has any resolvable identity, so a live pool
          cannot be destroyed by this tool. Always run with dry run first.
        </p>

        <div className={styles.formRow} style={{ marginBottom: 'var(--space-3)' }}>
          <label className={styles.formLabel} htmlFor="doids">
            DO IDs (one 64-char hex per line) — {doIds.length} valid
            {invalidLines.length > 0 && `, ${invalidLines.length} invalid`}
          </label>
          <textarea
            id="doids"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
            rows={12}
            className={styles.searchInput}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-xs)',
              resize: 'vertical',
            }}
          />
        </div>

        <div className={styles.searchBar}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-wide)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run
          </label>
          <button
            type="button"
            className={styles.actionButton}
            onClick={run}
            disabled={running || doIds.length === 0}
          >
            {running ? 'Running…' : dryRun ? 'Run dry-run' : `Wipe ${doIds.length}`}
          </button>
          {invalidLines.length > 0 && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-wide)',
              }}
            >
              {invalidLines.length} invalid line(s) ignored
            </span>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {summary && (
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              border: 'var(--border-width) solid var(--border)',
              background: 'var(--bg-secondary)',
              marginBottom: 'var(--space-4)',
              display: 'grid',
              gridTemplateColumns: 'repeat(5, auto)',
              gap: 'var(--space-5)',
              fontSize: 'var(--text-sm)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <Stat label="Requested" value={summary.requested} />
            <Stat label="Wiped" value={summary.wiped} />
            <Stat label="Refused" value={summary.refused} />
            <Stat label="Errors" value={summary.errors} />
            <Stat label="Dry run" value={summary.dryRun ? 'yes' : 'no'} />
          </div>
        )}

        {results.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>DO ID</th>
                <th>Result</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => {
                const isRefused = !r.ok && r.status === 409;
                const isWiped = r.ok && !r.dryRun;
                const isDry = r.ok && r.dryRun;
                const detail = r.error
                  ? r.error
                  : r.nameIdentity || r.storedIdentity
                    ? `identity: ${JSON.stringify(r.nameIdentity ?? r.storedIdentity)}`
                    : r.hadAlarm !== undefined
                      ? `alarm cleared: ${r.hadAlarm ? 'yes' : 'no'}`
                      : '—';
                return (
                  <tr key={r.doId}>
                    <td
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-xs)',
                      }}
                    >
                      {r.doId.slice(0, 16)}…
                    </td>
                    <td>
                      <span
                        className={isRefused ? styles.badgeInactive : styles.badgeActive}
                      >
                        {isWiped ? 'Wiped' : isDry ? 'Dry-run' : isRefused ? 'Refused' : 'Error'}
                      </span>
                    </td>
                    <td
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {r.status ?? '—'}
                    </td>
                    <td
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-secondary)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {detail}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontSize: 'var(--text-xs)',
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-wide)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--text-lg)',
          color: 'var(--text-primary)',
          fontWeight: 'var(--weight-bold)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
